/**
 * Perpendicular drags: every EDGE (primary wall, alcove sub-wall, chamfer
 * diagonal, free-element side) drags perpendicular to itself as one `perp`
 * gesture over a scalar ValueTarget, clamped by constraints.
 */

import {
  alcoveCornerLineFrame,
  alcoveDepthRange,
  AlcoveEdgeName,
  alcoveLeftWallRange,
  alcoveRightWallRange,
  alcoveWallLineFrame,
  clamp,
  EdgeName,
  FloorPlanEdgeSegment,
  FloorPlanFreeElement,
  FloorPlanRoom,
  freeElementRectangleCorners,
  freeElementRotateVector,
  freeElementSideNormal,
  geometryBounds,
  isHorizontal,
  LineFrame,
  minLength,
  minWidth,
  Point,
  primaryEdgeLineFrame,
  roomCornerLineFrame,
} from '@kaiizen/kai-floor-plan';
import { dot2d } from '../../math2d';
import { chamferData } from '../editorGeometry';
import type { ChamferData, EditorMutableState, Gesture, PerpDrag, Selection } from '../editorState';
import { snapEdges, snapSideSize, touchedOpenings } from '../elementSnapping';
import { select } from '../selection';
import { snapReach } from '../snapReach';
import { applyValue, chamferDiagValue, type ChamferSelection, isChamferSelection, type ValueTarget } from '../valueTargets';
import { beginDraft } from './draft';

type PerpendicularGesture = Extract<Gesture, { kind: 'perp' }>;
type FreeSideTarget = Extract<ValueTarget, { t: 'freeSide' }>;

function startPerpendicularDrag(state: EditorMutableState, perpendicularDrag: PerpDrag, target: ValueTarget): void {
  beginDraft(state);
  state.drag = { kind: 'perp', pd: perpendicularDrag, target };
}

/**
 * Drag a primary room edge perpendicular to itself (resizes width/length). An
 * origin-anchored edge (outward normal pointing toward negative axes) doesn't
 * move in model space as the value grows, so the viewport compensates to keep
 * the grabbed edge under the pointer. The edge's frame decides that, not its name.
 */
export function beginEdgeDrag(
  state: EditorMutableState,
  edge: EdgeName,
  gap: { gs: number; ge: number } | null,
  grabScreen: Point,
): void {
  if (gap) state.edgeGap = { edge, ...gap };
  select(state, { kind: 'edge', edge });
  const room = state.shape;
  const lineFrame = primaryEdgeLineFrame(room, edge);
  const horizontal = isHorizontal(edge);
  const anchored = lineFrame.perp.x + lineFrame.perp.y < 0;
  startPerpendicularDrag(state, {
    grabScreen,
    perp: lineFrame.perp,
    initialValue: horizontal ? room.length : room.width,
    min: horizontal ? minLength(room) : minWidth(room),
    max: Infinity,
    viewportComp: anchored ? lineFrame.perp : undefined,
    initialOffset: anchored ? { ...state.viewport.offset } : undefined,
  }, { t: horizontal ? 'roomLength' : 'roomWidth' });
}

/**
 * A chamfer diagonal's outward unit normal in room space. A free element's
 * diagonal normal is (-sx·dy, -sy·dx) in the element's local frame, rotated
 * into room space.
 */
function chamferDiagonalNormal(room: FloorPlanRoom, selection: ChamferSelection, chamfer: ChamferData): Point | null {
  if (selection.kind === 'corner') return roomCornerLineFrame(room, selection.corner)?.perp ?? null;
  if (selection.kind === 'alcoveCorner') return alcoveCornerLineFrame(room, selection.edge, selection.index, selection.which)?.perp ?? null;
  const element = room.freeElements?.[selection.index];
  const normal = { x: -chamfer.sx * chamfer.ch.dy, y: -chamfer.sy * chamfer.ch.dx };
  const length = Math.hypot(normal.x, normal.y);
  return element && length > 1e-9 ? freeElementRotateVector(element, { x: normal.x / length, y: normal.y / length }) : null;
}

/**
 * Drag a chamfer diagonal perpendicular to itself, scaling the cut with its
 * ratio preserved. Inward, the diagonal's outward normal negated, is a deeper cut.
 */
export function beginChamferDiagDrag(state: EditorMutableState, selection: Selection, grabScreen: Point): boolean {
  if (!isChamferSelection(selection)) return false;
  select(state, selection);
  const chamfer = chamferData(state, selection);
  if (!chamfer) return false;
  const { dx: initialDx, dy: initialDy } = chamfer.ch;
  const outward = chamferDiagonalNormal(state.shape, selection, chamfer);
  if (!outward) return false;
  const initialDiagonal = chamferDiagValue(initialDx, initialDy);
  const maxScale = Math.min(
    initialDx > 1e-6 ? chamfer.maxDx / initialDx : Infinity,
    initialDy > 1e-6 ? chamfer.maxDy / initialDy : Infinity,
  );
  startPerpendicularDrag(state, {
    grabScreen,
    perp: { x: -outward.x, y: -outward.y },
    initialValue: initialDiagonal,
    min: 0,
    max: initialDiagonal * maxScale,
  }, { t: 'chamferScale', sel: selection, dx0: initialDx, dy0: initialDy });
  return true;
}

/**
 * What dragging an alcove wall changes: the back wall its depth, the right
 * wall its width, the left wall its position with the far end fixed. Outward
 * on the left wall decreases the position, so its drag direction is flipped
 * to make a positive delta raise the value.
 */
function alcoveWallTarget(
  room: FloorPlanRoom,
  edge: EdgeName,
  storageIndex: number,
  wall: AlcoveEdgeName,
  lineFrame: LineFrame,
  alcove: FloorPlanEdgeSegment,
): { perp: Point; initialValue: number; range: { min: number; max: number }; target: ValueTarget } {
  if (wall === 'backEdge') {
    return {
      perp: lineFrame.perp,
      initialValue: alcove.depth ?? 0,
      range: alcoveDepthRange(room, edge, storageIndex),
      target: { t: 'alcoveDepth', edge, storageIndex },
    };
  }
  if (wall === 'leftEdge') {
    return {
      perp: { x: -lineFrame.perp.x, y: -lineFrame.perp.y },
      initialValue: alcove.position,
      range: alcoveLeftWallRange(room, edge, storageIndex),
      target: { t: 'alcoveLeft', edge, storageIndex, end: alcove.position + alcove.width },
    };
  }
  return {
    perp: lineFrame.perp,
    initialValue: alcove.position + alcove.width,
    range: alcoveRightWallRange(room, edge, storageIndex),
    target: { t: 'alcoveRight', edge, storageIndex },
  };
}

/** Drag an alcove sub-wall perpendicular to itself (back = depth, sides = mouth). */
export function beginAlcoveWallDrag(
  state: EditorMutableState,
  edge: EdgeName,
  storageIndex: number,
  wall: AlcoveEdgeName,
  gap: { gs: number; ge: number } | null,
  grabScreen: Point,
): void {
  state.wallGap = gap ? { edge, storageIndex, wall, ...gap } : null;
  select(state, { kind: 'alcoveWall', edge, index: storageIndex, wall });
  const room = state.shape;
  const lineFrame = alcoveWallLineFrame(room, edge, storageIndex, wall);
  const alcove = room[edge]?.segments[storageIndex];
  if (!lineFrame || !alcove) return;
  const { perp, initialValue, range, target } = alcoveWallTarget(room, edge, storageIndex, wall, lineFrame, alcove);
  startPerpendicularDrag(state, { grabScreen, perp, initialValue, min: range.min, max: range.max }, target);
}

/**
 * Drag one side of a free element perpendicular to itself, resizing it at any
 * rotation. The side's outward unit normal in room space doubles as the
 * screen drag direction, since screen axes parallel model axes. Growing by Δ
 * with the opposite side fixed moves the dragged side's two corners by
 * Δ·normal, so the maximum is where the first of them reaches the geometry
 * bounds. The minimum size is enforced by applyValue.
 */
export function beginFreeSideDrag(
  state: EditorMutableState,
  index: number,
  side: EdgeName,
  grabScreen: Point,
): void {
  select(state, { kind: 'inner', index });
  const element = state.shape.freeElements?.[index];
  if (!element) return;
  const bounds = geometryBounds(state.shape);
  const perp = freeElementSideNormal(element, side);
  const [backLeft, backRight, frontRight, frontLeft] = freeElementRectangleCorners(element);
  const moving = side === 'backEdge'
    ? [backLeft, backRight]
    : side === 'rightEdge'
    ? [backRight, frontRight]
    : side === 'frontEdge'
    ? [frontRight, frontLeft]
    : [frontLeft, backLeft];
  const travel = (point: Point): number =>
    Math.min(
      perp.x > 1e-9 ? (bounds.maxX - point.x) / perp.x : perp.x < -1e-9 ? (bounds.minX - point.x) / perp.x : Infinity,
      perp.y > 1e-9 ? (bounds.maxY - point.y) / perp.y : perp.y < -1e-9 ? (bounds.minY - point.y) / perp.y : Infinity,
    );
  const initialSize = side === 'backEdge' || side === 'frontEdge' ? element.length : element.width;
  startPerpendicularDrag(state, {
    grabScreen,
    perp,
    initialValue: initialSize,
    min: 0,
    max: initialSize + Math.min(travel(moving[0]), travel(moving[1])),
  }, {
    t: 'freeSide',
    index,
    side,
    size0: initialSize,
    cx0: element.x + element.width / 2,
    cy0: element.y + element.length / 2,
    ux: perp.x,
    uy: perp.y,
  });
}

/**
 * An unrotated element's side size snapped so its moving side lands on an
 * edge it would bump into, or on an opening's edge along a wall it is
 * against; the opposite side stays put.
 */
function snappedSideSize(state: EditorMutableState, target: FreeSideTarget, element: FloorPlanFreeElement, size: number): number {
  const { cx0, cy0, size0, ux, uy } = target;
  const edges = snapEdges(state.shape, target.index);
  const horizontal = Math.abs(ux) > 0.5;
  const outward = (horizontal ? ux : uy) > 0 ? 1 : -1;
  const fixed = (horizontal ? cx0 : cy0) - outward * size0 / 2;
  const openings = touchedOpenings(element, edges);
  const reach = snapReach(state.viewport.scale);
  return horizontal
    ? snapSideSize(size, fixed, outward, element.y, element.y + element.length, edges.xs, openings.xs, reach)
    : snapSideSize(size, fixed, outward, element.x, element.x + element.width, edges.ys, openings.ys, reach);
}

/**
 * Advance a perpendicular drag: the value moves by the pointer's screen
 * travel along the drag direction, clamped, with a free element's side
 * snapped when snapping is on. An anchored edge shifts the viewport so it
 * stays under the pointer.
 */
export function movePerpendicularDrag(state: EditorMutableState, gesture: PerpendicularGesture, screen: Point): void {
  const drag = gesture.pd;
  const target = gesture.target;
  const screenDelta = dot2d({ x: screen.x - drag.grabScreen.x, y: screen.y - drag.grabScreen.y }, drag.perp);
  let newValue = clamp(drag.initialValue + screenDelta / state.viewport.scale, drag.min, drag.max);
  const element = target.t === 'freeSide' ? state.shape.freeElements?.[target.index] : undefined;
  if (state.snapping && target.t === 'freeSide' && element && !element.rotation) {
    newValue = clamp(snappedSideSize(state, target, element, newValue), drag.min, drag.max);
  }
  state.draftShape = applyValue(state.shape, target, newValue);
  if (drag.viewportComp && drag.initialOffset) {
    const viewportDelta = (newValue - drag.initialValue) * state.viewport.scale;
    state.viewport.offset = {
      x: drag.initialOffset.x + drag.viewportComp.x * viewportDelta,
      y: drag.initialOffset.y + drag.viewportComp.y * viewportDelta,
    };
  }
}
